/**
 * Octopus — a rare benthic animal working the reef.
 *
 * Almost all of an octopus is motion, so the animation IS the design. The arm pose is a
 * closed-form curve evaluated per-vertex (see OctopusMaterial); this file owns the two things
 * a shader cannot: which behaviour it is in, and where its body actually goes.
 *
 * LOCOMOTION IS INTEGRATED, NOT SCRIPTED
 * Velocity is integrated rather than the body being pushed along a path, because propulsion
 * has to be a real impulse: an octopus fills its mantle, squeezes, and coasts. A jet is one
 * sharp pulse of thrust followed by a long glide against drag, and the mantle contraction,
 * the arms snapping streamlined and the acceleration all come from the same pulse.
 *
 * RARITY
 * It is not always present. Each individual is out foraging for a stretch and then back in a
 * den for far longer, so one is somewhere in the window under a tenth of the time and, with
 * fog, actually seen less often than that. An octopus you meet every time you swim past is
 * set dressing; one you meet every few minutes is an event.
 */
import * as THREE from 'three';
import { Rng } from '../core/Rng.ts';
import { OctopusMaterial } from '../render/OctopusMaterial.ts';
import { bedHeight, waterDepth, COAST } from '../world/Coast.ts';
import { buildOctopus } from '../world/octopus/voxelOctopus.js';
import type { SkyEnv } from '../world/DayNight.ts';

/** the generator's own voxel pitch, in MODEL units */
const VOX = 0.028;
const ARM_LEN = 1.85;
const MANTLE = 1.0;
/** model span, mantle top to arm tip, before scaling */
const MODEL_SPAN = (0.52 + 0.50) * MANTLE + ARM_LEN;
/** half a shark: the roster lands at ~8.1 u, so this animal spans ~4 u across its arms */
const WORLD_SPAN = 4.05;
const SCALE = WORLD_SPAN / MODEL_SPAN;

/** how far along the coast one may drift from the player before it is recycled */
const WINDOW_X = 150;
/* How long an individual stays out, and how long it is gone. Rarity is driven by a LIFETIME
   rather than by waiting to drift out of the window: at a cruise the window takes minutes to
   leave, so an octopus placed once would simply be permanent. A real one is out foraging for
   a while and then back in a den, which is both the right behaviour and the right cadence —
   these numbers put one somewhere in the window about a quarter of the time, and fog means
   you actually SEE one a good deal less often than that. */
const OUT_TIME: [number, number] = [35, 90];
const DEN_TIME: [number, number] = [260, 560];

/* Behaviour targets. Each state is a set of arm parameters; the live values chase them, so
   switching never snaps. */
interface Pose { bend: number; pitch: number; wave: number; wspd: number; wfq: number }
const BEH: Record<string, Pose> = {
  drift: { bend: 1.55, pitch: -0.30, wave: 0.14, wspd: 1.50, wfq: 1.35 },
  crawl: { bend: 0.95, pitch: -0.62, wave: 0.26, wspd: 2.40, wfq: 1.60 },
  /* Jetting: arms near straight and pitched steeply back, so they stream to full extension
     instead of splaying. A long wavelength at low rate is what makes the trail read as
     graceful rather than as a buzz. */
  jet: { bend: -0.12, pitch: -1.15, wave: 0.11, wspd: 1.05, wfq: 0.75 },
  land: { bend: 0.35, pitch: -0.75, wave: 0.12, wspd: 1.20, wfq: 1.15 },
  /* Curl: 2.75 lands the tips directly over the crown and clear of the mantle apex. Past
     about 3.3 the arc overshoots vertical and the tips come back DOWN alongside the head. */
  curl: { bend: 2.75, pitch: 0.20, wave: 0.06, wspd: 0.70, wfq: 1.10 },
};
/** arms flare wide and press down once it touches bottom */
const SETTLED: Pose = { bend: 0.55, pitch: -1.05, wave: 0.05, wspd: 0.8, wfq: 1.1 };

/* A benthic animal, so the cycle is weighted to the bottom: it crawls and sits far more than
   it swims, and a jet is an event. */
const ORDER = ['crawl', 'curl', 'crawl', 'drift', 'land', 'crawl', 'jet', 'land'];
const DWELL: Record<string, [number, number]> = {
  crawl: [10, 20], curl: [8, 16], drift: [6, 12], land: [5, 9], jet: [4, 7],
};

const JET_PERIOD = 2.6;
const TAU = Math.PI * 2;

const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
function sstep(e0: number, e1: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

interface Individual {
  root: THREE.Group;
  mesh: THREE.InstancedMesh;
  material: OctopusMaterial;
  p: THREE.Vector3;
  v: THREE.Vector3;
  aim: THREE.Vector3;
  beh: string;
  behIdx: number;
  dwell: number;
  live: Pose & { jet: number; open: number; crawl: number };
  jetPhase: number;
  jetPulse: number;
  jetOpen: number;
  settle: number;
  armPhase: number;
  chromPhase: number;
  /** seconds until it may appear again; > 0 means denned up */
  away: number;
  /** seconds left out before it retreats to a den */
  outFor: number;
  t: number;
}

export class Octopus {
  readonly group = new THREE.Group();

  private readonly individuals: Individual[] = [];
  private readonly rng: Rng;
  private readonly geometry: THREE.BufferGeometry;
  private readonly voxels: number;

  private readonly tmpA = new THREE.Vector3();
  private readonly tmpB = new THREE.Vector3();
  private readonly qt = new THREE.Quaternion();
  private readonly up = new THREE.Vector3(0, 1, 0);

  constructor(count = 2, seed = 0x0c7095) {
    this.rng = new Rng(seed >>> 0);
    this.group.name = 'octopus';

    const D = buildOctopus(this.rng.int(1, 1 << 28), VOX, ARM_LEN, MANTLE);
    this.voxels = D.n;

    /* One geometry, shared by every individual. The rest position is an instanced attribute
       rather than an instance MATRIX: the shader places each voxel itself, so a full 4x4 per
       voxel would be 13 floats of pure waste each. */
    this.geometry = new THREE.BoxGeometry(1, 1, 1);
    const rest = new Float32Array(D.n * 3);
    const off = new Float32Array(D.n * 2);
    for (let i = 0; i < D.n; i++) {
      rest[i * 3] = D.x[i];
      rest[i * 3 + 1] = D.y[i];
      rest[i * 3 + 2] = D.z[i];
      off[i * 2] = D.ox[i];
      off[i * 2 + 1] = D.oz[i];
    }
    this.geometry.setAttribute('aRest', new THREE.InstancedBufferAttribute(rest, 3));
    this.geometry.setAttribute('aOff', new THREE.InstancedBufferAttribute(off, 2));
    this.geometry.setAttribute('aArm', new THREE.InstancedBufferAttribute(D.arm, 1));
    this.geometry.setAttribute('aT', new THREE.InstancedBufferAttribute(D.t, 1));
    this.geometry.setAttribute('aRole', new THREE.InstancedBufferAttribute(D.role, 1));
    this.geometry.setAttribute('aShade', new THREE.InstancedBufferAttribute(D.shade, 1));

    for (let i = 0; i < count; i++) {
      const material = new OctopusMaterial(VOX, SCALE);
      material.uniforms.uLen.value = ARM_LEN;
      material.uniforms.uCrown.value = D.crown;
      material.uniforms.uMantleY.value = 0.52 * MANTLE;
      material.uniforms.uSkinMode.value = this.rng.int(1, 3);
      material.uniforms.uContrast.value = this.rng.range(0.55, 0.85);
      // Cool pigment, violet through indigo to cobalt: it has to sit apart from the warm reef
      // without going saturated enough to read as a toy.
      const h = this.rng.next();
      (material.uniforms.uPig.value as THREE.Color).setRGB(
        lerp(0.300, 0.230, h), lerp(0.170, 0.400, h), lerp(0.470, 0.615, h));
      (material.uniforms.uPig2.value as THREE.Color).setRGB(
        lerp(0.660, 0.640, h), lerp(0.560, 0.790, h), lerp(0.800, 0.895, h));

      const mesh = new THREE.InstancedMesh(this.geometry, material, D.n);
      // The shader ignores instanceMatrix entirely, but three still uploads and culls against
      // it, so it has to be a valid identity rather than left as zeros.
      const m = new THREE.Matrix4();
      for (let k = 0; k < D.n; k++) mesh.setMatrixAt(k, m);
      mesh.instanceMatrix.needsUpdate = true;
      mesh.frustumCulled = false;

      const root = new THREE.Group();
      root.scale.setScalar(SCALE);
      root.add(mesh);
      root.visible = false;
      this.group.add(root);

      this.individuals.push({
        root, mesh, material,
        p: new THREE.Vector3(), v: new THREE.Vector3(),
        aim: new THREE.Vector3(1, 0, 0.25).normalize(),
        beh: 'crawl', behIdx: 0, dwell: 0,
        live: { bend: 1.55, pitch: -0.30, wave: 0.14, wspd: 1.5, wfq: 1.35, jet: 0, open: 0, crawl: 0 },
        jetPhase: 0, jetPulse: 0, jetOpen: 0, settle: 0,
        armPhase: this.rng.range(0, TAU), chromPhase: this.rng.range(0, 40),
        away: this.rng.range(0, 260),
        outFor: 0,
        t: 0,
      });
    }
  }

  /** total voxels in one individual, for the debug overlay */
  get voxelCount(): number {
    return this.voxels;
  }

  get visibleCount(): number {
    return this.individuals.reduce((a, o) => a + (o.root.visible ? 1 : 0), 0);
  }

  /** the meshes a shadow pass should render; empty while nothing is out */
  casters(): THREE.InstancedMesh[] {
    return this.individuals.filter((o) => o.root.visible).map((o) => o.mesh);
  }

  update(dt: number, playerX: number, playerZ: number): void {
    for (const o of this.individuals) {
      if (o.away > 0) {
        o.away -= dt;
        if (o.away <= 0) this.place(o, playerX);
        else continue;
      }
      o.t += dt;
      o.outFor -= dt;
      this.behave(o, dt);
      this.locomote(o, dt);
      this.pose(o, dt);

      // Denned up when its shift ends, or when the coast has carried it out of range.
      if (o.outFor <= 0 || Math.abs(o.p.x - playerX) > WINDOW_X) this.den(o);
    }
    void playerZ;
  }

  /** back into a crevice, for a good long while */
  private den(o: Individual): void {
    o.root.visible = false;
    o.away = this.rng.range(DEN_TIME[0], DEN_TIME[1]);
  }

  /** drop an individual onto the reef somewhere ahead of or behind the player */
  private place(o: Individual, playerX: number): void {
    const raw = playerX + (this.rng.next() < 0.5 ? -1 : 1) * this.rng.range(70, WINDOW_X - 12);
    const x = Math.max(-COAST.lagoonHalf + 40, Math.min(COAST.lagoonHalf - 40, raw));
    // It lives on the reef, so it is placed in the reef band and then in whatever water is
    // actually there — the shoreline meanders, so the band alone is not a guarantee.
    let z = this.rng.range(COAST.reefFrom + 4, COAST.reefTo - 10);
    if (waterDepth(x, z) < COAST.minSwimDepth) z += 12;
    o.p.set(x, bedHeight(x, z) + 1.1, z);
    o.v.set(0, 0, 0);
    o.aim.set(this.rng.range(-1, 1), 0, this.rng.range(-1, 1)).normalize();
    o.beh = 'crawl';
    o.behIdx = 0;
    o.dwell = this.rng.range(8, 16);
    o.outFor = this.rng.range(OUT_TIME[0], OUT_TIME[1]);
    o.root.visible = true;
  }

  private behave(o: Individual, dt: number): void {
    o.dwell -= dt;
    if (o.dwell <= 0) {
      o.behIdx = (o.behIdx + 1) % ORDER.length;
      o.beh = ORDER[o.behIdx];
      const d = DWELL[o.beh];
      o.dwell = this.rng.range(d[0], d[1]);
    }
  }

  private locomote(o: Individual, dt: number): void {
    const gy = bedHeight(o.p.x, o.p.z);
    const clear = o.p.y - gy;

    /* Steer: wander, and stay on the reef. It is a benthic animal on a strip of coast, so the
       pull is cross-shore only — along the shore it is free to go wherever. */
    let wob = Math.sin(o.t * 0.29) * 0.7 + Math.sin(o.t * 0.17 + 1.3) * 0.5;
    if (o.beh === 'crawl') wob += Math.sin(o.t * 0.71 + 2.3) * 0.95;
    this.tmpA.set(-o.aim.z, 0, o.aim.x).multiplyScalar(wob * 0.5);
    let pull = 0;
    if (o.p.z < COAST.reefFrom) pull = Math.min(1.4, (COAST.reefFrom - o.p.z) * 0.10);
    else if (o.p.z > COAST.reefTo) pull = -Math.min(1.4, (o.p.z - COAST.reefTo) * 0.10);
    const shallow = COAST.minSwimDepth - waterDepth(o.p.x, o.p.z);
    if (shallow > 0) pull += shallow * 0.6;
    this.tmpA.add(this.tmpB.set(0, 0, pull)).add(o.aim);
    if (this.tmpA.lengthSq() > 1e-6) {
      o.aim.lerp(this.tmpA.normalize(), Math.min(1, dt * 0.9)).normalize();
    }

    o.jetPulse = 0;
    o.jetOpen = 0;
    o.settle = 0;

    if (o.beh === 'jet') {
      o.jetPhase += dt / JET_PERIOD;
      const cp = o.jetPhase % 1;
      /* Evaluate each stage at cp and cp+1 so the tails wrap continuously across the cycle
         boundary instead of clipping to zero. */
      const g = (c: number, mu: number, sd: number): number => Math.exp(-Math.pow((c - mu) / sd, 2));
      o.jetOpen = Math.max(g(cp, 0.55, 0.155), g(cp + 1, 0.55, 0.155));   // refill, arms open
      o.jetPulse = Math.max(g(cp, 0.86, 0.085), g(cp + 1, 0.86, 0.085));  // contraction
      o.v.addScaledVector(o.aim, o.jetPulse * 9.0 * dt);
      o.v.y += o.jetPulse * 0.45 * dt - (o.p.y - (gy + 3.2)) * 0.55 * dt;
      // a spread bell brakes and lifts — that rise IS the pre-propulsion stage
      o.v.y += o.jetOpen * 0.9 * dt;
    } else if (o.beh === 'crawl') {
      o.v.addScaledVector(o.aim, 0.5 * dt);
      o.v.y += Math.max(-1, Math.min(1, gy + 0.85 - o.p.y)) * 1.8 * dt;   // hug the bottom
    } else if (o.beh === 'land') {
      o.settle = sstep(1.20, 0.55, clear);
      o.v.addScaledVector(o.aim, 0.18 * dt * (1 - o.settle));
      // Descend at a held rate and only damp on the last stretch — scaling the target by
      // (1 - settle) makes it asymptote and never touch down.
      const want = clear > 0.85 ? -1.6 : -0.32;
      o.v.y += (want - o.v.y) * 3.2 * dt;
      if (clear < 0.55) { o.v.multiplyScalar(1 - Math.min(1, dt * 6)); o.v.y *= 0.4; }
    } else if (o.beh === 'curl') {
      o.v.y -= 0.24 * dt;                                                 // a balled octopus sinks
    } else {
      o.v.addScaledVector(o.aim, 0.6 * dt);
      o.v.y += (Math.sin(o.t * 0.5) * 0.4 - (o.p.y - (gy + 2.6)) * 0.5 - o.v.y * 0.4) * dt;
    }

    /* Drag. High while gliding is what makes a jet read as a burst and fade, and open arms are
       a parachute — that extra drag is what separates the wind-up from the glide instead of
       both just coasting. */
    const drag = o.beh === 'jet' ? 0.72 + o.jetOpen * 1.75 : 2.1;
    o.v.multiplyScalar(Math.exp(-drag * dt));
    o.p.addScaledVector(o.v, dt);

    const floorY = gy + 0.45;
    if (o.p.y < floorY) { o.p.y = floorY; if (o.v.y < 0) o.v.y *= -0.15; }
    const ceil = gy + 7.0;
    if (o.p.y > ceil) { o.p.y = ceil; if (o.v.y > 0) o.v.y = 0; }

    /* Orientation: the mantle leads. An escaping octopus streams its arms behind it, so the
       body axis swings from world-up toward the direction of travel as the jet builds, and
       returns upright as it coasts. */
    const sp = o.v.length();
    this.tmpA.copy(o.v);
    if (sp > 0.05) this.tmpA.multiplyScalar(1 / sp);
    else this.tmpA.copy(this.up);
    this.tmpB.copy(this.up).lerp(this.tmpA, Math.min(0.92, o.live.jet * 0.85 + sstep(0.6, 2.4, sp) * 0.5)).normalize();
    this.qt.setFromUnitVectors(this.up, this.tmpB);
    o.root.quaternion.slerp(this.qt, Math.min(1, dt * 3));
    o.root.position.copy(o.p);
  }

  private pose(o: Individual, dt: number): void {
    const T = BEH[o.beh];
    const ease = (r: number): number => 1 - Math.exp(-r * dt);
    const k = ease(2.0);

    // Landing blends the reaching pose into a settled one as it nears the bottom.
    const s = o.settle;
    o.live.bend = lerp(o.live.bend, lerp(T.bend, SETTLED.bend, s), k);
    o.live.pitch = lerp(o.live.pitch, lerp(T.pitch, SETTLED.pitch, s), k);
    o.live.wave = lerp(o.live.wave, lerp(T.wave, SETTLED.wave, s), k);
    o.live.wspd = lerp(o.live.wspd, lerp(T.wspd, SETTLED.wspd, s), k);
    o.live.wfq = lerp(o.live.wfq, lerp(T.wfq, SETTLED.wfq, s), k);
    // The arms LAG the impulse — water drags them back, they do not snap — and opening is
    // unhurried while closing is drawn in ahead of the push.
    o.live.jet = lerp(o.live.jet, o.jetPulse, ease(o.jetPulse > o.live.jet ? 4.0 : 1.4));
    o.live.open = lerp(o.live.open, o.jetOpen, ease(o.jetOpen > o.live.open ? 1.9 : 2.8));
    o.live.crawl = lerp(o.live.crawl, o.beh === 'crawl' ? 1 : 0, ease(1.6));

    o.armPhase += dt * o.live.wspd;
    o.chromPhase += dt * 0.85;

    const u = o.material.uniforms;
    u.uTime.value = o.t;
    u.uPhase.value = o.armPhase;
    u.uChromT.value = o.chromPhase;
    u.uBend.value = o.live.bend;
    u.uPitch.value = o.live.pitch;
    // arms flex more as they spread
    u.uWave.value = o.live.wave * (1 + o.live.open * 0.55);
    u.uWaveFq.value = o.live.wfq;
    u.uJet.value = o.live.jet;
    u.uOpen.value = o.live.open;
    u.uCrawl.value = o.live.crawl;
  }

  /** feed the wake, but only while it is actually out */
  trackInto(wake: { track(k: number, x: number, y: number, z: number, r: number, t: number): void },
            base: number, now: number): void {
    for (let i = 0; i < this.individuals.length; i++) {
      const o = this.individuals[i];
      if (o.root.visible) wake.track(base + i, o.p.x, o.p.y, o.p.z, 2.2, now);
    }
  }

  setEnv(env: SkyEnv): void {
    for (const o of this.individuals) o.material.setEnv(env);
  }

  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    for (const o of this.individuals) o.material.setShadow(map, matrix, strength);
  }

  debugRow(): string {
    const out = this.visibleCount;
    if (out === 0) {
      const soon = Math.min(...this.individuals.map((o) => o.away));
      return `denned (${soon.toFixed(0)}s)`;
    }
    return `${out} · ${this.individuals.find((o) => o.root.visible)?.beh ?? ''}`;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.geometry.dispose();
    for (const o of this.individuals) {
      o.mesh.dispose();
      o.material.dispose();
    }
    this.individuals.length = 0;
  }
}
